import { TextField, TextFieldProps } from "@mui/material";
import { DatePickerProps } from "@mui/x-date-pickers";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";

interface Props extends Omit<DatePickerProps<DateTime, DateTime>, "renderInput" | "value" | "onChange"> {
  /** Props to pass through to the rendered input. */
  inputProps?: Omit<TextFieldProps, "onChange">;
  /** The controlled iso date value of the picker. Must pass a `string` if controlled, else `undefined` if uncontrolled. */
  value?: string;
  /** Callback when the string value of the render input changes. */
  onChange?: (stringDate: string) => void;
  /** Callback when the datepicker has a new valid `DateTime` value. */
  onAccepted?: (date?: DateTime) => void;
}

/** A helper to facilitate proper integration of the x-date-picker `DatePicker` with `Luxon`. */
const DatePicker: React.FC<Props> = ({ value, mask, inputFormat, inputProps, onAccepted, onChange, ...rest }) => {
  const [internalValue, setInternalValue] = useState("");

  useEffect(() => {
    if (value !== undefined) setInternalValue(value);
  }, [value]);
  const onFieldChange = (fieldValue: string) => {
    onChange && onChange(fieldValue);
    if (!fieldValue && onAccepted) onAccepted(undefined);
    setInternalValue(fieldValue);
  };
  const onPickerChange = (date: DateTime | null) => {
    if (!date || date.isValid) {
      onAccepted && onAccepted(date || undefined);
      onChange && onChange(date?.toISO() || "");
      setInternalValue(date?.toISODate() || "");
    }
  };

  return (
    <MuiDatePicker
      {...rest}
      renderInput={props => <TextField {...props} {...inputProps} onChange={e => onFieldChange(e.target.value)} />}
      value={internalValue ? DateTime.fromISO(internalValue) : DateTime.now()}
      onChange={onPickerChange}
      // mask and inputFormat are a required workaround for a bug in LuxonUtils for masking to function. CKH 06/2022
      mask={mask || "__/__/____"}
      inputFormat={inputFormat || "MM/dd/yyyy"}
    />
  );
};

export default DatePicker;
